/**
 * BinomDraggableItemRow — sortable lander/offer row with preview overlay.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil, Trash2 } from 'lucide-react';
import type { FlowLayer } from '@trafficpulse/shared';

import { Button } from '@/components/ui/layout/Button';
import { Switch } from '@/components/ui/form/controls/Switch';
import { cn } from '@/lib/utils';
import {
  usePreviewMode,
  usePreviewRow,
} from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import PreviewAwareWeightSlot from '@/features/flow-optimizer/components/preview/PreviewAwareWeightSlot';
import PreviewExcludedIndicator from '@/features/flow-optimizer/components/preview/PreviewExcludedIndicator';
import { FlowTagChips } from '@/features/flow-optimizer/components/preview/FlowTagChips';
import { excludedReasonText, showExcludedBadge } from '@/features/flow-optimizer/components/preview/preview-row.types';

import type { BinomDraftItem } from '../../binom-flow-draft.types';

export interface BinomDraggableItemRowProps {
  id: string;
  item: BinomDraftItem;
  layer: 'landers' | 'offers';
  pathId: string;
  onWeightChange: (weight: number) => void;
  onActiveChange: (active: boolean) => void;
  onRemove: () => void;
  onEdit?: () => void;
  dragDisabled?: boolean;
}

const BinomDraggableItemRow: FC<BinomDraggableItemRowProps> = ({
  id,
  item,
  layer,
  pathId,
  onWeightChange,
  onActiveChange,
  onRemove,
  onEdit,
  dragDisabled = false,
}) => {
  const previewMode = usePreviewMode();
  const previewRow = usePreviewRow(layer as FlowLayer, item.id, pathId);
  const previewLocked =
    previewMode && previewRow != null && !showExcludedBadge(previewRow, item.active);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled: dragDisabled });
  const style = { transform: CSS.Transform.toString(transform), transition };

  const hoverName = item.name?.trim() ? item.name : `Item ${item.id}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      role="listitem"
      className={cn(
        'flex items-center gap-2 px-1.5 py-1',
        !item.active && 'opacity-70',
        isDragging && 'opacity-60 ring-1 ring-primary/40',
      )}
    >
      {!dragDisabled ? (
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Drag ${item.name || item.id}`}
          className="shrink-0 cursor-grab text-muted-foreground/60 hover:text-foreground active:cursor-grabbing"
        >
          <GripVertical className="h-3 w-3" aria-hidden />
        </button>
      ) : (
        <span className="w-3 shrink-0" aria-hidden />
      )}
      <span className="flex min-w-0 flex-1 items-center gap-1">
        <span className="min-w-0 truncate text-xs text-foreground">
          {item.name?.trim() ? item.name : `Item ${item.id}`}
        </span>
        <FlowTagChips layer={layer} itemId={item.id} pathId={pathId} className="shrink-0 gap-1" />
        {showExcludedBadge(previewRow, item.active) ? (
          <PreviewExcludedIndicator
            title={excludedReasonText(previewRow)}
            row={previewRow}
            name={hoverName}
          />
        ) : null}
      </span>
      <div className={cn('flex items-center gap-1', previewLocked && 'min-w-[12rem] justify-end')}>
        <PreviewAwareWeightSlot
          name={hoverName}
          previewRow={previewRow}
          previewMode={previewMode}
          active={item.active}
          value={item.weight}
          onChange={onWeightChange}
          ariaLabel={`Weight for ${item.name || item.id}`}
        />
      </div>
      {onEdit ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
          onClick={onEdit}
          aria-label={`Edit ${item.name || item.id}`}
        >
          <Pencil className="h-3 w-3" aria-hidden />
        </Button>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 text-destructive hover:bg-destructive/10"
        onClick={() => {
          if (window.confirm(`Remove "${item.name || item.id}"?`)) onRemove();
        }}
        aria-label={`Remove ${item.name || item.id}`}
      >
        <Trash2 className="h-3 w-3" aria-hidden />
      </Button>
      <Switch
        checked={item.active}
        onCheckedChange={onActiveChange}
        aria-label={`${item.active ? 'Disable' : 'Enable'} ${item.name || item.id}`}
      />
    </div>
  );
};

export default BinomDraggableItemRow;
